// MA-780 (render half) — which Journey stones render in the BIG "frontier"
// language (92px, pulsing halo, "today" ribbon stroke), plus the DOM hook that
// names the frontier stones by id.
//
// THE RULE (decision recorded in docs/build/specs/MA-780-journey-out-of-window-
// frontier.md): the big frontier language is a promise that a tap adds the
// stone to the day's picks. A stone the fold calls a frontier (practicing /
// next) that a tap would not pick (out of the D4 window, or a foundation stone
// below the placement floor: resolveStoneTap(..., progress) is not "navigate")
// cannot keep that promise, so it renders in the normal
// non-pickable style instead. Its STATUS is untouched, so every spoken and aria
// string (KID_STATUS_LABEL via speakTextForStone / ariaLabelFor) is the same
// word it always was — this module adds no kid-audible string.
//
// Pure: no React, no DOM.
import type { Status } from "./skillTreeStatus";
import type { JourneyStone, JourneyView } from "./journeyView";
import { resolveStoneTap, type StoneTapProgressLike } from "./journeyTap";

/** The fold's frontier statuses (the same two JourneyPath.tsx keys its
 *  frontier counts off). */
export const FRONTIER_STATUSES: ReadonlySet<Status> = new Set<Status>(["practicing", "next"]);

export function isFrontierStatus(status: Status): boolean {
  return FRONTIER_STATUSES.has(status);
}

/** The ONE pick predicate: the tap handler's own (resolveStoneTap with the
 *  child's progress, so the placement floor counts). StoneRow's `pickEligible`
 *  and the paint below both come from this. */
export function isStonePickEligible(
  stone: Pick<JourneyStone, "id" | "tapEligibility">,
  progress: StoneTapProgressLike | null | undefined,
  registryAge?: number | null,
  valveLowered = false,
): boolean {
  return resolveStoneTap({ id: stone.id, tapEligibility: stone.tapEligibility }, progress, registryAge, valveLowered).kind === "navigate";
}

/** True iff this stone renders big in the frontier style: a frontier STATUS
 *  that a tap would actually pick (`pickEligible`). */
export function rendersAsFrontier(stone: Pick<JourneyStone, "status">, pickEligible: boolean): boolean {
  return isFrontierStatus(stone.status) && pickEligible;
}

/** The status the ribbon colours a stone by: a frontier that is not pickable
 *  reads as a future stone (`locked`), never the "today" stroke. */
export function ribbonStatusFor(stone: Pick<JourneyStone, "status">, pickEligible: boolean): Status {
  return isFrontierStatus(stone.status) && !pickEligible ? "locked" : stone.status;
}

/** Ids (only ids) of every frontier-status stone per lane, in chapter order.
 *  Frontier here is the FOLD's meaning (practicing/next), not the render
 *  style: the live-QA "at most one frontier stone per chain" rule reads this. */
export function frontierStoneIds(view: JourneyView): { reading: string[]; math: string[] } {
  const reading: string[] = [];
  const math: string[] = [];
  for (const chapter of view.chapters) {
    for (const s of chapter.readingStones) if (isFrontierStatus(s.status)) reading.push(s.id);
    for (const s of chapter.mathStones) if (isFrontierStatus(s.status)) math.push(s.id);
  }
  return { reading, math };
}

/** data-journey-frontier-ids = "reading:<id>,<id>|math:<id>,<id>" — ids only. */
export function formatFrontierStoneIds(view: JourneyView): string {
  const { reading, math } = frontierStoneIds(view);
  return `reading:${reading.join(",")}|math:${math.join(",")}`;
}
